import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import '../../assets/styles/CircularTestimonials.css';

function calculateGap(width) {
  const minWidth = 1024;
  const maxWidth = 1456;
  const minGap = 60;
  const maxGap = 86;
  if (width <= minWidth) return minGap;
  if (width >= maxWidth) return Math.max(minGap, maxGap + 0.06018 * (width - maxWidth));
  return minGap + (maxGap - minGap) * ((width - minWidth) / (maxWidth - minWidth));
}

const quoteVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 },
};

const CircularTestimonials = ({ testimonials, autoplay = true, labels = {} }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(1200);

  const imageContainerRef = useRef(null);
  const autoplayIntervalRef = useRef(null);

  const total = useMemo(() => testimonials.length, [testimonials]);
  const active = useMemo(() => testimonials[activeIndex], [activeIndex, testimonials]);

  useEffect(() => {
    const handleResize = () => {
      if (imageContainerRef.current) {
        setContainerWidth(imageContainerRef.current.offsetWidth);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const stopAutoplay = useCallback(() => {
    if (autoplayIntervalRef.current) {
      clearInterval(autoplayIntervalRef.current);
      autoplayIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!autoplay || reduced) return undefined;

    autoplayIntervalRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, 5000);

    return stopAutoplay;
  }, [autoplay, total, stopAutoplay]);

  const handleNext = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % total);
    stopAutoplay();
  }, [total, stopAutoplay]);

  const handlePrev = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + total) % total);
    stopAutoplay();
  }, [total, stopAutoplay]);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      handlePrev();
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      handleNext();
    }
  };

  const getImageStyle = (index) => {
    const focus = testimonials[index].focus;
    const framing = focus ? { objectPosition: `center ${focus}` } : {};
    const gap = calculateGap(containerWidth);
    const stickUp = gap * 0.8;
    const isActive = index === activeIndex;
    const isLeft = (activeIndex - 1 + total) % total === index;
    const isRight = (activeIndex + 1) % total === index;

    if (isActive) {
      return {
        ...framing,
        zIndex: 3,
        opacity: 1,
        transform: 'translateX(0px) translateY(0px) scale(1) rotateY(0deg)',
      };
    }
    if (isLeft) {
      return {
        ...framing,
        zIndex: 2,
        opacity: 1,
        transform: `translateX(-${gap}px) translateY(-${stickUp}px) scale(0.85) rotateY(15deg)`,
      };
    }
    if (isRight) {
      return {
        ...framing,
        zIndex: 2,
        opacity: 1,
        transform: `translateX(${gap}px) translateY(-${stickUp}px) scale(0.85) rotateY(-15deg)`,
      };
    }
    return { ...framing, zIndex: 1, opacity: 0, pointerEvents: 'none' };
  };

  if (!total) return null;

  return (
    <div
      className="ct"
      role="group"
      aria-roledescription="carrusel"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <div className="ct__grid">
        <div className="ct__images" ref={imageContainerRef}>
          {testimonials.map((item, index) => (
            <img
              key={item.src}
              src={item.src}
              alt={item.name}
              className="ct__image"
              style={getImageStyle(index)}
              aria-hidden={index !== activeIndex}
            />
          ))}
        </div>

        <div className="ct__content">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndex}
              variants={quoteVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.3, ease: 'easeInOut' }}
            >
              <h3 className="ct__name">{active.name}</h3>
              <p className="ct__designation">{active.designation}</p>

              <p className="ct__quote">
                {active.quote.split(' ').map((word, i) => (
                  <motion.span
                    key={`${activeIndex}-${i}`}
                    initial={{ filter: 'blur(10px)', opacity: 0, y: 5 }}
                    animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
                    transition={{ duration: 0.22, ease: 'easeInOut', delay: 0.025 * i }}
                    className="ct__word"
                  >
                    {word}&nbsp;
                  </motion.span>
                ))}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="ct__arrows">
            <button
              type="button"
              className="ct__arrow"
              onClick={handlePrev}
              aria-label={labels.prev || 'Anterior'}
            >
              <ArrowLeft size={24} aria-hidden="true" />
            </button>
            <button
              type="button"
              className="ct__arrow"
              onClick={handleNext}
              aria-label={labels.next || 'Siguiente'}
            >
              <ArrowRight size={24} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CircularTestimonials;
